import React, {useState, useRef, useEffect, useCallback} from 'react';
import {View, Text, TextInput, TouchableOpacity, StyleSheet} from 'react-native';
import {createPortal} from 'react-dom';

export const NoteCell = ({value, orderId, updateOrderInfo}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');
  const [editValue, setEditValue] = useState('');
  const [tooltipPos, setTooltipPos] = useState({x: 0, y: 0});
  const inputRef = useRef(null);
  const hasSaved = useRef(false);

  useEffect(() => {
    setCurrentValue(value || '');
    setIsEditing(false);
    hasSaved.current = false;
  }, [value, orderId]);

  const enterEditMode = e => {
    e?.stopPropagation?.();
    hasSaved.current = false;
    setEditValue(currentValue);
    setIsEditing(true);
    setIsHovered(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (typeof inputRef.current.select === 'function') {
          inputRef.current.select();
        }
      }
    }, 30);
  };

  const handleSave = useCallback(e => {
    e?.stopPropagation?.();
    if (hasSaved.current) return;
    hasSaved.current = true;
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed === currentValue) return;
    const prevValue = currentValue;
    setCurrentValue(trimmed);
    updateOrderInfo(orderId, {order: {notes_internal: trimmed}}, () =>
      setCurrentValue(prevValue)
    );
  }, [editValue, currentValue, orderId, updateOrderInfo]);

  useEffect(() => {
    if (!isEditing || typeof document === 'undefined') return undefined;

    const handleOutsideInteraction = event => {
      if (event.target?.closest?.('[data-testid="noteCellInput"]')) return;
      handleSave(event);
    };

    document.addEventListener('mousedown', handleOutsideInteraction, true);
    document.addEventListener('touchstart', handleOutsideInteraction, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, true);
      document.removeEventListener('touchstart', handleOutsideInteraction, true);
    };
  }, [isEditing, handleSave]);

  const handleKeyPress = e => {
    if (e.nativeEvent?.key === 'Enter') {
      handleSave(e);
    } else if (e.nativeEvent?.key === 'Escape') {
      e?.stopPropagation?.();
      hasSaved.current = true;
      setEditValue(currentValue);
      setIsEditing(false);
    }
  };

  const handleMouseEnter = e => {
    setIsHovered(true);
    const clientX = e.nativeEvent?.clientX ?? 0;
    const clientY = e.nativeEvent?.clientY ?? 0;
    setTooltipPos({x: clientX, y: clientY});
  };

  const handleContextMenu = e => {
    e.preventDefault();
    enterEditMode(e);
  };

  if (isEditing) {
    return (
      <TextInput
        testID="noteCellInput"
        ref={inputRef}
        style={styles.input}
        value={editValue}
        onChangeText={setEditValue}
        onKeyPress={handleKeyPress}
        onBlur={handleSave}
        onSubmitEditing={handleSave}
        blurOnSubmit
        onPress={e => e?.stopPropagation?.()}
      />
    );
  }

  return (
    <>
      {isHovered && currentValue && createPortal(
        <div style={{...tooltipStyle, top: tooltipPos.y - 44, left: tooltipPos.x}}>
          {currentValue}
        </div>,
        document.body
      )}
      <TouchableOpacity
        testID="noteCell"
        style={styles.container}
        activeOpacity={0.85}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
        onPress={enterEditMode}
        onContextMenu={handleContextMenu}>
        <Text numberOfLines={1} style={styles.noteText}>
          {currentValue}
        </Text>
        {isHovered && (
          <TouchableOpacity
            testID="noteCellEditButton"
            onPress={enterEditMode}
            style={styles.editButton}>
            <i className="icon-edit" style={editIconStyle} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    minHeight: 28,
    width: '100%',
    cursor: 'pointer',
  },
  input: {
    color: '#e8e8e8',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#4BDA9C',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    width: '100%',
    outlineStyle: 'none',
  },
  noteText: {
    flex: 1,
    color: '#e8e8e8',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    minHeight: 18,
  },
  editButton: {
    marginLeft: 4,
    padding: 2,
  },
});

const tooltipStyle = {
  position: 'fixed',
  zIndex: 99999,
  backgroundColor: '#000',
  color: '#e8e8e8',
  fontFamily: 'Poppins_300Light, Poppins, sans-serif',
  fontSize: 12,
  padding: '5px 10px',
  borderRadius: 4,
  maxWidth: 320,
  pointerEvents: 'none',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  boxShadow: '0 2px 8px rgba(0,0,0,0.65)',
};

const editIconStyle = {
  fontSize: 13,
  color: '#acbbcb',
  cursor: 'pointer',
};
